import { FC, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { InnerBox } from "@/components/elements/InnerBox";
import { Grid } from "@mui/material";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { TCompanyForm } from "@/features/company/types/companyForm";
import { Select } from "@/components/elements/Select";
import { SelectMulti } from "@/components/elements/SelectMulti";
import { formatCompanyId } from "@/utils/number";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { useBusinessClassificationSync } from "@/features/company/hooks/useBusinessClassificationSync";
import { TOption } from "@/types/common";

type TCompanyBasicInfoFormProps = {
  companyId?: string;
};

/**
 * Exclude 特定産業分野 options already chosen in the other rows so the same industry can't be picked twice.
 * Number()-coerce both sides because edit-mode (loaded) values may arrive as strings while option values are numbers.
 */
const excludeTakenIndustries = (options: TOption[], takenValues: Array<number | null | undefined>): TOption[] =>
  options.filter((opt) => !takenValues.some((taken) => taken != null && Number(taken) === Number(opt.value)));

export const CompanyBasicInfoForm: FC<TCompanyBasicInfoFormProps> = ({ companyId }) => {
  const { companySpecificIndustryOptions, isLoading: isLoadingCompanySpecificIndustry } = useGetCompanySpecificIndustryList();
  const methods = useFormContext<TCompanyForm>();
  const { control } = methods;

  const [selectedIndustryId1, selectedIndustryId2, selectedIndustryId3] = useWatch({
    control,
    name: ["companySpecificIndustryId1", "companySpecificIndustryId2", "companySpecificIndustryId3"],
  });

  // Filter industry options for each select, excluding values already selected in the other rows.
  const optionsForIndustryId1 = useMemo(
    () => excludeTakenIndustries(companySpecificIndustryOptions, [selectedIndustryId2, selectedIndustryId3]),
    [companySpecificIndustryOptions, selectedIndustryId2, selectedIndustryId3],
  );

  const optionsForIndustryId2 = useMemo(
    () => excludeTakenIndustries(companySpecificIndustryOptions, [selectedIndustryId1, selectedIndustryId3]),
    [companySpecificIndustryOptions, selectedIndustryId1, selectedIndustryId3],
  );

  const optionsForIndustryId3 = useMemo(
    () => excludeTakenIndustries(companySpecificIndustryOptions, [selectedIndustryId1, selectedIndustryId2]),
    [companySpecificIndustryOptions, selectedIndustryId1, selectedIndustryId2],
  );

  // Sync each 業務区分 field with its row's industry (options + auto-select/reset + disabled/readonly).
  const {
    options: optionsForBusinessClassification1,
    disabled: disabledBusinessClassification1,
    readonly: readonlyBusinessClassification1,
  } = useBusinessClassificationSync(selectedIndustryId1, "companySpecificBusinessClassificationIds1");
  const {
    options: optionsForBusinessClassification2,
    disabled: disabledBusinessClassification2,
    readonly: readonlyBusinessClassification2,
  } = useBusinessClassificationSync(selectedIndustryId2, "companySpecificBusinessClassificationIds2");
  const {
    options: optionsForBusinessClassification3,
    disabled: disabledBusinessClassification3,
    readonly: readonlyBusinessClassification3,
  } = useBusinessClassificationSync(selectedIndustryId3, "companySpecificBusinessClassificationIds3");

  return (
    <InnerBox title="基本情報">
      <Grid container rowSpacing={2} columnSpacing={3}>
        <Grid item xs={6}>
          <FormField label="組織ID（自動生成）">
            <Input readonly placeholder={companyId ? formatCompanyId(companyId) : "保存後に自動生成されます"} />
          </FormField>
        </Grid>
        <Grid item xs={6}></Grid>
        <Grid item xs={6}>
          <FormField control={control} name="companyName">
            <Input />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="companyKanaName">
            <Input />
          </FormField>
        </Grid>

        <Grid item xs={6}>
          <FormField control={control} name="companySpecificIndustryId1">
            <Select placeholder="選択してください" loading={isLoadingCompanySpecificIndustry} options={optionsForIndustryId1} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="companySpecificBusinessClassificationIds1">
            <SelectMulti
              placeholder="選択してください"
              loading={isLoadingCompanySpecificIndustry}
              disabled={disabledBusinessClassification1}
              readonly={readonlyBusinessClassification1}
              options={optionsForBusinessClassification1}
            />
          </FormField>
        </Grid>

        <Grid item xs={6}>
          <FormField control={control} name="companySpecificIndustryId2">
            <Select placeholder="選択してください" loading={isLoadingCompanySpecificIndustry} options={optionsForIndustryId2} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="companySpecificBusinessClassificationIds2">
            <SelectMulti
              placeholder="選択してください"
              loading={isLoadingCompanySpecificIndustry}
              disabled={disabledBusinessClassification2}
              readonly={readonlyBusinessClassification2}
              options={optionsForBusinessClassification2}
            />
          </FormField>
        </Grid>

        <Grid item xs={6}>
          <FormField control={control} name="companySpecificIndustryId3">
            <Select placeholder="選択してください" loading={isLoadingCompanySpecificIndustry} options={optionsForIndustryId3} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="companySpecificBusinessClassificationIds3">
            <SelectMulti
              placeholder="選択してください"
              loading={isLoadingCompanySpecificIndustry}
              disabled={disabledBusinessClassification3}
              readonly={readonlyBusinessClassification3}
              options={optionsForBusinessClassification3}
            />
          </FormField>
        </Grid>
      </Grid>
    </InnerBox>
  );
};
